import React, { useRef } from 'react';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// =============== PDF STYLES ===============
const pdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    backgroundColor: '#1a472a',
    padding: 15,
    marginBottom: 20,
    textAlign: 'center',
  },
  headerTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  headerSubtitle: {
    color: '#e0e0e0',
    fontSize: 12,
  },
  section: {
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#cccccc',
    borderRadius: 5,
    overflow: 'hidden',
  },
  sectionTitle: {
    backgroundColor: '#f0f0f0',
    padding: 8,
    fontSize: 12,
    fontWeight: 'bold',
    borderBottomWidth: 1,
    borderBottomColor: '#2e7d32',
  },
  sectionContent: {
    padding: 10,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
  },
  gridItem: {
    width: '50%',
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    width: '40%',
    fontWeight: 'bold',
    fontSize: 9,
  },
  value: {
    width: '60%',
    fontSize: 9,
  },
  table: {
    marginTop: 5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ddd',
    paddingVertical: 4,
  },
  tableHeader: {
    backgroundColor: '#e8f5e9',
    fontWeight: 'bold',
  },
  col1: { width: '25%', paddingRight: 4 },
  col2: { width: '20%', paddingRight: 4 },
  col3: { width: '15%', paddingRight: 4 },
  col4: { width: '15%', paddingRight: 4 },
  col5: { width: '25%', paddingRight: 4 },
  footer: {
    marginTop: 20,
    textAlign: 'center',
    fontSize: 8,
    color: '#666',
    borderTopWidth: 1,
    borderTopColor: '#ccc',
    paddingTop: 10,
  },
  badge: {
    backgroundColor: '#c8e6c9',
    padding: '2 6',
    borderRadius: 10,
    fontSize: 8,
  },
  statusSuccess: {
    color: '#2e7d32',
    fontWeight: 'bold',
  },
  statusWarning: {
    color: '#ed6c02',
    fontWeight: 'bold',
  },
  statusError: {
    color: '#d32f2f',
    fontWeight: 'bold',
  },
  deficiencyTable: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#cccccc',
  },
  deficiencyHeader: {
    backgroundColor: '#ffebee',
    fontWeight: 'bold',
    padding: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
  },
  deficiencyRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ddd',
    paddingVertical: 4,
  },
  deficiencyCol1: { width: '15%', paddingLeft: 5 },
  deficiencyCol2: { width: '40%', paddingLeft: 5 },
  deficiencyCol3: { width: '45%', paddingLeft: 5 },
});

// =============== Helper function to format values ===============
const formatValue = (value) => {
  if (value === undefined || value === null || value === '') return 'Not Specified';
  if (value === 'YES' || value === 'Yes') return '✅ Yes';
  if (value === 'NO' || value === 'No') return '❌ No';
  return value;
};

const getStatusStyle = (value) => {
  if (value === 'YES' || value === 'Yes') return pdfStyles.statusSuccess;
  if (value === 'NO' || value === 'No') return pdfStyles.statusError;
  return {};
};

// =============== PDF DOCUMENT COMPONENT ===============
export const PDFReport = ({ data, data2 }) => {
  const collegeData = data?.data?.[0] || {};
  const buildAreas = data?.buildArea || [];
  const parking = data?.parking?.[0] || {};
  const ugCourses = data?.ugCourses || [];
  const corpusFund = data?.corpusfundUGPG || [];
  const corpusInstall = data?.corpusFundInstall || [];

  // NOC Inspection Data from apiData2
  const nocData = data2?.[0] || {};

  // Helper to render any field if exists
  const renderField = (label, key, isBooleanStatus = false) => {
    const value = nocData[key];
    if (value === undefined || value === null || value === '') return null;
    return (
      <View style={pdfStyles.gridItem}>
        <Text style={pdfStyles.label}>{label}:</Text>
        <Text style={[pdfStyles.value, isBooleanStatus ? getStatusStyle(value) : {}]}>
          {formatValue(value)}
        </Text>
      </View>
    );
  };

  // Section: Society & Registration (Rows 2-9 from form)
  const renderSocietyRegistration = () => {
    const fields = [
      { label: 'Society Resolution for Shifting', key: 'socity_for_shifting' },
      { label: 'Society Registered under Act', key: 'registration_act' },
      { label: 'Bye-laws Enclosed', key: 'by_laws' },
      { label: 'Permission Order Enclosed', key: 'enclosed_or_not' },
      { label: 'University Affiliation Order (2025-26)', key: 'affiliation_order_2025_26' },
      { label: 'Last EoP Granted Enclosed', key: 'eop_granted' },
      { label: 'Year of Establishment', key: 'establishment' },
      { label: 'College Location Type', value: collegeData.college_location_type },
    ];
    const hasAny = fields.some(f => f.key ? nocData[f.key] : f.value);
    if (!hasAny) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>🏛️ Society & Registration Details</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.grid}>
            {fields.map((f, idx) => {
              if (f.key) return renderField(f.label, f.key, true);
              if (f.value) return (
                <View key={idx} style={pdfStyles.gridItem}>
                  <Text style={pdfStyles.label}>{f.label}:</Text>
                  <Text style={pdfStyles.value}>{formatValue(f.value)}</Text>
                </View>
              );
              return null;
            })}
          </View>
        </View>
      </View>
    );
  };

  // Section: Land Details (Row 10 a-h)
  const renderLandDetails = () => {
    const fields = [
      { label: 'Society has own land as per norms', value: collegeData.has_own_land },
      { label: 'Registered ownership of land deed produced', key: 'ownership_of_the' },
      { label: 'Survey No', value: collegeData.land_survey_no },
      { label: 'Village/Town', value: collegeData.land_village },
      { label: 'Land Mandal', value: collegeData.land_mandal },
      { label: 'Land District', value: collegeData.land_district },
      { label: 'Registered document of land in Society name', key: 'owned_name' },
      { label: 'Own land provided in same Mandal', key: 'same_mandal' },
      { label: 'Distance of land from college building (km)', value: collegeData.land_distance },
      { label: 'Sketch plan by licensed surveyor attested by MRO', key: 'licensed_surveyor' },
      { label: 'Schedule tallying with sketch plan', key: 'sketch_plan' },
    ];
    const hasAny = fields.some(f => f.key ? nocData[f.key] : f.value);
    if (!hasAny) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>🗺️ Land Details</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.grid}>
            {fields.map((f, idx) => {
              if (f.key) return renderField(f.label, f.key, true);
              if (f.value) return (
                <View key={idx} style={pdfStyles.gridItem}>
                  <Text style={pdfStyles.label}>{f.label}:</Text>
                  <Text style={pdfStyles.value}>{formatValue(f.value)}</Text>
                </View>
              );
              return null;
            })}
          </View>
        </View>
      </View>
    );
  };

  // Section: Building Accommodation (Row 11 a-u)
  const renderBuildingDetails = () => {
    const fields = [
      { label: 'Nature of Shifting', key: 'nature_of_shifting' },
      { label: 'Proposed to be shifted within same Mandal', key: 'proposed_to_be_shifted' },
      { label: 'Permanent independent building with RCC roof', key: 'building_is_permanent' },
      { label: 'Located 500m away from Cinema/Wine shops etc.', key: 'located_500_mtrs' },
      { label: 'Building Type (Leased/Own)', key: 'building_is_leased' },
      { label: 'Lease document enclosed (if leased)', key: 'lease_doc_enclosed' },
      { label: 'Lease document in Society name', key: 'lease_in_society_name' },
      { label: 'Year of registration of lease document', value: collegeData.lease_reg_year },
      { label: 'Validity of lease period', value: collegeData.lease_validity },
      { label: 'Years completed in leased building', value: collegeData.lease_years_completed },
      { label: 'Remaining years of lease', value: collegeData.lease_years_remaining },
      { label: 'Sale deed uploaded (if own)', key: 'sale_deed_uploaded' },
      { label: 'Sale deed in Society name', key: 'sale_deed_society_name' },
      { label: 'Building plan approved by competent authority', key: 'building_accommodation_approved' },
      { label: 'Plan drawn by licensed surveyor attested by Principal', key: 'drawn_by_licensed' },
      { label: 'Plinth area of Building (sft)', value: collegeData.plinth_area },
      { label: 'Construction permission order uploaded', key: 'construction_permission' },
      { label: 'Valid Sanitary Certificate uploaded', key: 'sanitary_ertificate' },
      { label: 'Geotagged photographs attested by Principal', key: 'geotagged_photographs' },
      { label: 'Any other institution running in same building', key: 'is_nstitution_running' },
      { label: 'NoC from authority to utilize part of building', key: 'authority_to_utilize' },
      { label: 'Annexure-I details match stipulated norms', key: 'existing_building' },
    ];
    const hasAny = fields.some(f => f.key ? nocData[f.key] : f.value);
    if (!hasAny) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>🏢 New Building Accommodation</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.grid}>
            {fields.map((f, idx) => {
              if (f.key) return renderField(f.label, f.key, true);
              if (f.value) return (
                <View key={idx} style={pdfStyles.gridItem}>
                  <Text style={pdfStyles.label}>{f.label}:</Text>
                  <Text style={pdfStyles.value}>{formatValue(f.value)}</Text>
                </View>
              );
              return null;
            })}
          </View>
        </View>
      </View>
    );
  };

  // Section: Parking & Playground (Rows 12,13)
  const renderParkingPlayground = () => {
    const hasData = parking && Object.keys(parking).length > 0;
    const hasFormData = nocData.playground_facility || nocData.alternative_arrangement;
    if (!hasData && !hasFormData) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>🚗 Parking & Playground Facilities</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.grid}>
            <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Parking available in same premises:</Text><Text style={pdfStyles.value}>{parking.parking_provided ? 'Yes' : 'No'}</Text></View>
            <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>20% of built-up area provided for parking:</Text><Text style={pdfStyles.value}>{parking.builtup_area_20percent ? 'Yes' : 'No'}</Text></View>
            <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Parking area (sq.m):</Text><Text style={pdfStyles.value}>{parking.parking_area || '-'}</Text></View>
            <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Total built-up area (sq.m):</Text><Text style={pdfStyles.value}>{parking.total_builtup_area || '-'}</Text></View>
            {renderField('Playground facility in same premises', 'playground_facility', true)}
            {nocData.playground_facility === 'Yes' && (
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Extent of playground (sft):</Text><Text style={pdfStyles.value}>{parking.playground_distance || '-'}</Text></View>
            )}
            {renderField('Alternative playground within 5 km with bus facility', 'alternative_arrangement', true)}
            {nocData.alternative_arrangement === 'Yes' && (
              renderField('MoU for alternative playground produced', 'playground_mou', true)
            )}
            {renderField('Valid Fire Safety Certificate / Affidavit', 'fire_safety_certificate', true)}
          </View>
        </View>
      </View>
    );
  };

  // Section: Corpus Fund (Exemption and Installments)
  const renderCorpusFund = () => {
    const hasExemption = nocData.corpus_fund !== undefined;
    const hasInstallments = corpusInstall && corpusInstall.length > 0;
    if (!hasExemption && !hasInstallments) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>💰 Corpus Fund Details</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.grid}>
            {renderField('College exempted from paying Corpus Fund', 'corpus_fund', true)}
            {nocData.corpus_fund === 'Yes' && renderField('Rural/Tribal area certificate produced', 'certificate_issued', true)}
            {renderField('Corpus Fund Extra (Deficiency)', 'corpus_fund_extra')}
          </View>
          {hasInstallments && (
            <>
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginTop: 10, marginBottom: 5 }}>Installments for Colleges established before 2021-22</Text>
              <View style={pdfStyles.table}>
                <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                  <Text style={{ width: '20%' }}>Particulars</Text>
                  <Text style={{ width: '26%' }}>First Installment</Text>
                  <Text style={{ width: '27%' }}>Second Installment</Text>
                  <Text style={{ width: '27%' }}>Third Installment</Text>
                </View>
                {[
                  { label: 'Amount', keys: ['amount'] },
                  { label: 'Period (from - to)', keys: ['from_date', 'to_date'], formatter: (inst) => `${inst.from_date || '-'} to ${inst.to_date || '-'}` },
                  { label: 'FDR/Bank Guarantee No.', keys: ['fdr_bank_number'] },
                  { label: 'Date of FDR', keys: ['date_of_fdr'] },
                  { label: 'Bank Name & Address', keys: ['bank_address'] },
                  { label: 'IFSC Code', keys: ['ifsc'] },
                  { label: 'Date of Maturity', keys: ['date_of_maturity'] },
                  { label: 'Date of Renewal', keys: ['date_of_renewal'] },
                ].map((row, idx) => {
                  const install1 = corpusInstall.find(i => i.installment_no === 1) || {};
                  const install2 = corpusInstall.find(i => i.installment_no === 2) || {};
                  const install3 = corpusInstall.find(i => i.installment_no === 3) || {};
                  const getValue = (inst) => {
                    if (row.formatter) return row.formatter(inst);
                    return inst[row.keys[0]] || '-';
                  };
                  return (
                    <View key={idx} style={pdfStyles.tableRow}>
                      <Text style={{ width: '20%' }}>{row.label}</Text>
                      <Text style={{ width: '26%' }}>{getValue(install1)}</Text>
                      <Text style={{ width: '27%' }}>{getValue(install2)}</Text>
                      <Text style={{ width: '27%' }}>{getValue(install3)}</Text>
                    </View>
                  );
                })}
              </View>
            </>
          )}
        </View>
      </View>
    );
  };

  // Section: Infrastructure & Academic Facilities (Lab, Library, Staff)
  const renderInfraAcademic = () => {
    const fields = [
      { label: 'Lab equipment provided for all subjects', key: 'lab_equipment_provided' },
      { label: 'Subjects lacking equipment', key: 'lab_lack_equipments' },
      { label: 'Library books provided for all subjects', key: 'library_books_provided' },
      { label: 'Subjects lacking books', key: 'library_lack_equipments' },
      { label: 'Adequate staff as per UGC norms', key: 'staff_ugc_norms' },
      { label: 'Staff deficiencies', key: 'staff_teaching_deficiencies' },
      { label: 'Scholarship details as per Annexure-III', key: 'staff_as_per_annexure' },
      { label: 'College secured University ranks last year', key: 'staff_university_rank' },
      { label: 'Extension activities conducted last year', key: 'staff_extension_activities' },
      { label: 'College has functional website', key: 'staff_functional_website' },
    ];
    const hasAny = fields.some(f => nocData[f.key] !== undefined);
    if (!hasAny) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>📚 Infrastructure & Academic Facilities</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.grid}>
            {fields.map((f, idx) => renderField(f.label, f.key, true))}
          </View>
        </View>
      </View>
    );
  };

  // Section: Final Remarks & Status
  const renderFinalStatus = () => {
    if (!nocData.final_remarks && !nocData.final_status) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.sectionTitle}>✅ Final Decision</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.grid}>
            <View style={pdfStyles.gridItem}>
              <Text style={pdfStyles.label}>Final Remarks:</Text>
              <Text style={pdfStyles.value}>{nocData.final_remarks || 'Not Specified'}</Text>
            </View>
            {renderField('Final Status', 'final_status')}
            {nocData.final_status === 'DeficiencyIdentified' && renderField('Processing Fee', 'processing_fee')}
          </View>
        </View>
      </View>
    );
  };

  // Section: Deficiencies Table (when final_status is DeficiencyIdentified)
  const renderDeficienciesTable = () => {
    if (nocData.final_status !== 'DeficiencyIdentified') return null;
    const deficiencies = [
      { sno: 1, particular: 'Processing fee', value: nocData.processing_fee },
      { sno: 2, particular: 'Establishment', value: nocData.establishment },
      { sno: 3, particular: 'Affiliation order (2025-26)', value: nocData.affiliation_order_2025_26 },
      { sno: 4, particular: 'Society', value: nocData.society },
      { sno: 5, particular: 'Land', value: nocData.land },
      { sno: 6, particular: 'Building', value: nocData.building },
      { sno: 7, particular: 'Sanitary', value: nocData.sanitary },
      { sno: 8, particular: 'Parking', value: nocData.parking },
      { sno: 9, particular: 'Playground', value: nocData.playground },
      { sno: 10, particular: 'Fire safety', value: nocData.fire_safety },
      { sno: 11, particular: 'Corpus fund', value: nocData.corpus_fund_extra },
      { sno: 12, particular: 'Other deficiencies', value: nocData.other_deficiencies },
    ];
    const hasAny = deficiencies.some(d => d.value && d.value.trim() !== '');
    if (!hasAny) return null;
    return (
      <View style={pdfStyles.section}>
        <Text style={[pdfStyles.sectionTitle, { backgroundColor: '#ffebee', color: '#c62828' }]}>⚠️ Deficiencies Identified</Text>
        <View style={pdfStyles.sectionContent}>
          <View style={pdfStyles.deficiencyTable}>
            <View style={[pdfStyles.deficiencyRow, pdfStyles.deficiencyHeader]}>
              <Text style={pdfStyles.deficiencyCol1}>S.No</Text>
              <Text style={pdfStyles.deficiencyCol2}>Particulars</Text>
              <Text style={pdfStyles.deficiencyCol3}>Deficiencies</Text>
            </View>
            {deficiencies.map((def, idx) => (
              def.value && def.value.trim() !== '' && (
                <View key={idx} style={pdfStyles.deficiencyRow}>
                  <Text style={pdfStyles.deficiencyCol1}>{def.sno}</Text>
                  <Text style={pdfStyles.deficiencyCol2}>{def.particular}</Text>
                  <Text style={pdfStyles.deficiencyCol3}>{def.value}</Text>
                </View>
              )
            ))}
          </View>
        </View>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.headerTitle}>{rowData?.inst_name}</Text>
          <Text style={pdfStyles.headerSubtitle}>MANGALAGIRI | ACHARYA NAGARJUNA UNIVERSITY</Text>  
          <Text style={{ color: 'white', fontSize: 8, marginTop: 5 }}>Shifting Scrutiny Report - {new Date().toLocaleDateString()}</Text>
        </View>

        {/* College Basic Information */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>📋 College Basic Information</Text>
          <View style={pdfStyles.sectionContent}>
            <View style={pdfStyles.grid}>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>College Name:</Text><Text style={pdfStyles.value}>{collegeData.inst_name}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Existing Address:</Text><Text style={pdfStyles.value}>{collegeData.inst_address}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Society Name:</Text><Text style={pdfStyles.value}>{collegeData.society_name}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Transaction ID:</Text><Text style={pdfStyles.value}>{collegeData.transaction_id || '-'}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Year of Establishment:</Text><Text style={pdfStyles.value}>{collegeData.year_of_establishment}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>University:</Text><Text style={pdfStyles.value}>{collegeData.ub_name}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Mandal:</Text><Text style={pdfStyles.value}>{collegeData.mandal_name}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>District:</Text><Text style={pdfStyles.value}>{collegeData.district_name}</Text></View>
            </View>
          </View>
        </View>

        {/* New Address of Proposed Shift */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>📍 New Address of Degree College Proposed to Shift</Text>
          <View style={pdfStyles.sectionContent}>
            <View style={pdfStyles.grid}>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>New Mandal:</Text><Text style={pdfStyles.value}>{collegeData.new_mandal || '-'}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>New District:</Text><Text style={pdfStyles.value}>{collegeData.new_district || '-'}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>New Pincode:</Text><Text style={pdfStyles.value}>{collegeData.new_pincode || '-'}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Address of New Location:</Text><Text style={pdfStyles.value}>{collegeData.address_ofnew_location || '-'}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Shift Type:</Text><Text style={pdfStyles.value}>{collegeData.shift_type || '-'}</Text></View>
              <View style={pdfStyles.gridItem}><Text style={pdfStyles.label}>Distance from Existing:</Text><Text style={pdfStyles.value}>{collegeData.distance_from_existing || '-'} km</Text></View>
            </View>
          </View>
        </View>

        {/* All Form Sections mapped from ShiftingScrutinyReport */}
        {renderSocietyRegistration()}
        {renderLandDetails()}
        {renderBuildingDetails()}

        {/* Instructional Area / Buildings (UG & PG Accommodation) */}
        {buildAreas.length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>🏛️ Instructional Area / Buildings (UG & PG)</Text>
            <View style={pdfStyles.sectionContent}>
              <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                <Text style={pdfStyles.col1}>Block Name</Text>
                <Text style={pdfStyles.col2}>Particular</Text>
                <Text style={pdfStyles.col3}>Area (sq.m)</Text>
                <Text style={pdfStyles.col4}>Rooms</Text>
                <Text style={pdfStyles.col5}>Dimensions (L×B)</Text>
              </View>
              {buildAreas.map((building, idx) => (
                <View key={idx} style={pdfStyles.tableRow}>
                  <Text style={pdfStyles.col1}>{building.build_block_name}</Text>
                  <Text style={pdfStyles.col2}>{building.particular_name}</Text>
                  <Text style={pdfStyles.col3}>{building.area}</Text>
                  <Text style={pdfStyles.col4}>{building.no_of_rooms}</Text>
                  <Text style={pdfStyles.col5}>{building.length}×{building.breadth}m</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* UG & PG Programmes Details */}
        {ugCourses.length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>🎓 UG Programmes Details</Text>
            <View style={pdfStyles.sectionContent}>
              <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                <Text style={{ width: '50%' }}>Course Category</Text>
                <Text style={{ width: '25%' }}>Programs</Text>
                <Text style={{ width: '25%' }}>Total Seats</Text>
              </View>
              {ugCourses.map((course, idx) => (
                <View key={idx} style={pdfStyles.tableRow}>
                  <Text style={{ width: '50%' }}>{course.course_cat_name}</Text>
                  <Text style={{ width: '25%' }}>{course.count}</Text>
                  <Text style={{ width: '25%' }}>{course.sum}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* PG Programmes if available */}
        {data?.pgCourses && data.pgCourses.length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>🎓 PG Programmes Details</Text>
            <View style={pdfStyles.sectionContent}>
              <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                <Text style={{ width: '50%' }}>Course Category</Text>
                <Text style={{ width: '25%' }}>Programs</Text>
                <Text style={{ width: '25%' }}>Total Seats</Text>
              </View>
              {data.pgCourses.map((course, idx) => (
                <View key={idx} style={pdfStyles.tableRow}>
                  <Text style={{ width: '50%' }}>{course.course_cat_name}</Text>
                  <Text style={{ width: '25%' }}>{course.count}</Text>
                  <Text style={{ width: '25%' }}>{course.sum}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {renderParkingPlayground()}
        {renderCorpusFund()}
        {renderInfraAcademic()}
        {renderFinalStatus()}
        {renderDeficienciesTable()}

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>College Code: {nocData.coll_code || collegeData.coll_code || '24380'} | Generated on {new Date().toLocaleString()}</Text>
          <Text>This is a system-generated shifting scrutiny report from affiliation records.</Text>
        </View>
      </Page>
    </Document>
  );
};

// =============== WEB VIEW COMPONENT (for on-screen display) ===============
export const WebViewReport = ({ data, data2,rowData,data3 }) => {
  const collegeData = data?.data?.[0] || {};
  const buildAreas = data?.buildArea || [];
  const parking = data?.parking?.[0] || {};
  const ugCourses = data?.ugCourses || [];
  const corpusFund = data?.corpusfundUGPG || [];
  const corpusInstall = data?.corpusFundInstall || [];

  // NOC Inspection Data from apiData2
  const nocData = data2?.[0] || {};

  const formatValue = (value) => {
    if (value === undefined || value === null || value === '') return 'Not Specified';
    if (value === 'YES' || value === 'Yes') return '✅ Yes';
    if (value === 'NO' || value === 'No') return '❌ No';
    return value;
  };

  const getStatusClass = (value) => {
    if (value === 'YES' || value === 'Yes') return 'status-success';
    if (value === 'NO' || value === 'No') return 'status-error';
    return '';
  };

  // Helper to render field groups in web view
  const renderFieldGroupWeb = (title, fields) => {
    const hasData = fields.some(field => nocData[field.key] !== undefined && nocData[field.key] !== '');
    if (!hasData) return null;

    return (
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>{title}</h3>
        <div style={webStyles.grid}>
          {fields.map((field, idx) => {
            const value = nocData[field.key];
            if (value === undefined || value === null || value === '') return null;
            return (
              <div key={idx}>
                <strong>{field.label}:</strong>{' '}
                <span className={getStatusClass(value)}>{formatValue(value)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Deficiencies table for web view
  const renderDeficienciesWeb = () => {
    if (nocData.final_status !== 'DeficiencyIdentified') return null;
    const deficiencies = [
      { sno: 1, particular: 'Processing fee', value: nocData.processing_fee },
      { sno: 2, particular: 'Establishment', value: nocData.establishment },
      { sno: 3, particular: 'Affiliation order (2025-26)', value: nocData.affiliation_order_2025_26 },
      { sno: 4, particular: 'Society', value: nocData.society },
      { sno: 5, particular: 'Land', value: nocData.land },
      { sno: 6, particular: 'Building', value: nocData.building },
      { sno: 7, particular: 'Sanitary', value: nocData.sanitary },
      { sno: 8, particular: 'Parking', value: nocData.parking },
      { sno: 9, particular: 'Playground', value: nocData.playground },
      { sno: 10, particular: 'Fire safety', value: nocData.fire_safety },
      { sno: 11, particular: 'Corpus fund', value: nocData.corpus_fund_extra },
      { sno: 12, particular: 'Other deficiencies', value: nocData.other_deficiencies },
    ];
    const hasAny = deficiencies.some(d => d.value && d.value.trim() !== '');
    if (!hasAny) return null;
    return (
      <div style={{ ...webStyles.section, borderColor: '#ffcdd2' }}>
        <h3 style={{ ...webStyles.sectionTitle, backgroundColor: '#ffebee', color: '#c62828' }}>⚠️ Deficiencies Identified</h3>
        <table style={webStyles.table}>
          <thead><tr><th>S.No</th><th>Particulars</th><th>Deficiencies</th></tr></thead>
          <tbody>
            {deficiencies.map((def, idx) => (
              def.value && def.value.trim() !== '' && (
                <tr key={idx}><td>{def.sno}</td><td>{def.particular}</td><td>{def.value}</td></tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={webStyles.container}>
      <div style={webStyles.header}>
        {/* <h1 style={webStyles.headerTitle}>VIJAYA JYOTHI DEGREE COLLEGE</h1> */}
        <h1 style={webStyles.headerTitle}>
          {rowData?.inst_name} 
        </h1>
        <h3 style={webStyles.headerSubtitle}>({rowData?.coll_code})</h3>
        <p style={webStyles.headerDate}>Shifting Scrutiny Report - {new Date().toLocaleDateString()}</p>
      </div>

      {/* Basic Info */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>📋 College Basic Information</h3>
        <div style={webStyles.grid}>
          <div><strong>College Name:</strong> {collegeData.inst_name}</div>
          <div><strong>Existing Address:</strong> {collegeData.inst_address}</div>
          <div><strong>Society Name:</strong> {collegeData.society_name}</div>
          <div><strong>Transaction ID:</strong> {collegeData.transaction_id || '-'}</div>
          <div><strong>Year of Establishment:</strong> {collegeData.year_of_establishment}</div>
          <div><strong>University:</strong> {collegeData.ub_name}</div>
          <div><strong>Mandal:</strong> {collegeData.mandal_name}</div>
          <div><strong>District:</strong> {collegeData.district_name}</div>
        </div>
      </div>

      {/* New Address */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>📍 New Address of Proposed Shift</h3>
        <div style={webStyles.grid}>
          <div><strong>New Mandal:</strong> {collegeData.new_mandal || '-'}</div>
          <div><strong>New District:</strong> {collegeData.new_district || '-'}</div>
          <div><strong>New Pincode:</strong> {collegeData.new_pincode || '-'}</div>
          <div><strong>Address:</strong> {collegeData.address_ofnew_location || '-'}</div>
          <div><strong>Shift Type:</strong> {collegeData.shift_type || '-'}</div>
          <div><strong>Distance from Existing:</strong> {collegeData.distance_from_existing || '-'} km</div>
        </div>
      </div>

      {/* Society & Registration */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>🏛️ Society & Registration Details</h3>
        <div style={webStyles.grid}>
          <div><strong>Society Resolution for Shifting:</strong> {formatValue(nocData.socity_for_shifting)}</div>
          <div><strong>Society Registered under Act:</strong> {formatValue(nocData.registration_act)}</div>
          <div><strong>Bye-laws Enclosed:</strong> {formatValue(nocData.by_laws)}</div>
          <div><strong>Permission Order Enclosed:</strong> {formatValue(nocData.enclosed_or_not)}</div>
          <div><strong>University Affiliation Order (2025-26):</strong> {formatValue(nocData.atest_university)}</div>
          <div><strong>Last EoP Granted Enclosed:</strong> {formatValue(nocData.eop_granted)}</div>
          <div><strong>Year of Establishment:</strong> {formatValue(nocData.year_of_establishment)}</div>
        </div>
      </div>

      {/* Land Details */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>🗺️ Land Details</h3>
        <div style={webStyles.grid}>
          <div><strong>Society has own land as per norms:</strong> {collegeData.has_own_land || '-'}</div>
          <div><strong>Registered ownership deed produced:</strong> {formatValue(nocData.ownership_of_the)}</div>
          <div><strong>Survey No:</strong> {collegeData.land_survey_no || '-'}</div>
          <div><strong>Village/Town:</strong> {collegeData.land_village || '-'}</div>
          <div><strong>Land Mandal:</strong> {collegeData.land_mandal || '-'}</div>
          <div><strong>Land District:</strong> {collegeData.land_district || '-'}</div>
          <div><strong>Registered document in Society name:</strong> {formatValue(nocData.owned_name)}</div>
          <div><strong>Own land in same Mandal:</strong> {formatValue(nocData.same_mandal)}</div>
          <div><strong>Distance from building (km):</strong> {collegeData.land_distance || '-'}</div>
          <div><strong>Sketch plan by licensed surveyor:</strong> {formatValue(nocData.licensed_surveyor)}</div>
          <div><strong>Schedule tallying with sketch plan:</strong> {formatValue(nocData.sketch_plan)}</div>
        </div>
      </div>

      {/* Building Accommodation */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>🏢 New Building Accommodation</h3>
        <div style={webStyles.grid}>
          <div><strong>Nature of Shifting:</strong> {formatValue(nocData.nature_of_shifting)}</div>
          <div><strong>Shift within same Mandal:</strong> {formatValue(nocData.proposed_to_be_shifted)}</div>
          <div><strong>Permanent building with RCC roof:</strong> {formatValue(nocData.building_is_permanent)}</div>
          <div><strong>500m away from nuisance:</strong> {formatValue(nocData.located_500_mtrs)}</div>
          <div><strong>Building Type:</strong> {formatValue(nocData.building_is_leased)}</div>
          <div><strong>Lease document enclosed:</strong> {formatValue(nocData.lease_doc_enclosed)}</div>
          <div><strong>Lease in Society name:</strong> {formatValue(nocData.lease_in_society_name)}</div>
          <div><strong>Sale deed uploaded:</strong> {formatValue(nocData.sale_deed_uploaded)}</div>
          <div><strong>Sale deed in Society name:</strong> {formatValue(nocData.sale_deed_society_name)}</div>
          <div><strong>Building plan approved:</strong> {formatValue(nocData.building_accommodation_approved)}</div>
          <div><strong>Plan drawn by licensed surveyor:</strong> {formatValue(nocData.drawn_by_licensed)}</div>
          <div><strong>Construction permission:</strong> {formatValue(nocData.construction_permission)}</div>
          <div><strong>Sanitary Certificate:</strong> {formatValue(nocData.sanitary_ertificate)}</div>
          <div><strong>Geotagged photographs:</strong> {formatValue(nocData.geotagged_photographs)}</div>
          <div><strong>Other institution in same building:</strong> {formatValue(nocData.is_nstitution_running)}</div>
          <div><strong>NoC to utilize part of building:</strong> {formatValue(nocData.authority_to_utilize)}</div>
          <div><strong>Annexure-I matches norms:</strong> {formatValue(nocData.existing_building)}</div>
        </div>
      </div>

      {/* Buildings Table */}
      {buildAreas.length > 0 && (
        <div style={webStyles.section}>
          <h3 style={webStyles.sectionTitle}>🏛️ Instructional Area / Buildings</h3>
          <table style={webStyles.table}>
            <thead><tr><th>Block Name</th><th>Particular</th><th>Area (sq.m)</th><th>Rooms</th><th>Dimensions</th></tr></thead>
            <tbody>
              {buildAreas.map((b, i) => (
                <tr key={i}><td>{b.build_block_name}</td><td>{b.particular_name}</td><td>{b.area}</td><td>{b.no_of_rooms}</td><td>{b.length}×{b.breadth}m</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* UG Courses */}
      {ugCourses.length > 0 && (
        <div style={webStyles.section}>
          <h3 style={webStyles.sectionTitle}>🎓 UG Programmes</h3>
          <table style={webStyles.table}>
            <thead><tr><th>Course</th><th>Programs</th><th>Seats</th></tr></thead>
            <tbody>
              {ugCourses.map((c, i) => (<tr key={i}><td>{c.course_cat_name}</td><td>{c.count}</td><td>{c.sum}</td></tr>))}
            </tbody>
          </table>
        </div>
      )}

      {/* Parking & Playground */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>🚗 Parking & Playground</h3>
        <div style={webStyles.grid}>
          <div><strong>Parking available:</strong> {parking.parking_provided ? 'Yes' : 'No'}</div>
          <div><strong>20% built-up area for parking:</strong> {parking.builtup_area_20percent ? 'Yes' : 'No'}</div>
          <div><strong>Parking area (sq.m):</strong> {parking.parking_area || '-'}</div>
          <div><strong>Playground in same premises:</strong> {formatValue(nocData.playground_facility)}</div>
          <div><strong>Alternative playground:</strong> {formatValue(nocData.alternative_arrangement)}</div>
          <div><strong>Fire Safety Certificate:</strong> {formatValue(nocData.fire_safety_certificate)}</div>
        </div>
      </div>

      {/* Corpus Fund */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>💰 Corpus Fund</h3>
        <div style={webStyles.grid}>
          <div><strong>Exempted from Corpus Fund:</strong> {formatValue(nocData.corpus_fund)}</div>
          <div><strong>Rural/Tribal certificate:</strong> {formatValue(nocData.certificate_issued)}</div>
        </div>
        {corpusInstall.length > 0 && (
          <table style={webStyles.table}>
            <thead><tr><th>Installment</th><th>Amount</th><th>Period</th><th>Bank</th><th>Maturity Date</th></tr></thead>
            <tbody>
              {corpusInstall.map((inst, i) => (
                <tr key={i}><td>{inst.installment_no}</td><td>₹{inst.amount}</td><td>{inst.from_date} to {inst.to_date}</td><td>{inst.bank_address}</td><td>{inst.date_of_maturity}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Infrastructure */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>📚 Infrastructure & Academic Facilities</h3>
        <div style={webStyles.grid}>
          <div><strong>Lab equipment provided:</strong> {formatValue(nocData.lab_equipment_provided)}</div>
          <div><strong>Subjects lacking equipment:</strong> {nocData.lab_lack_equipments || '-'}</div>
          <div><strong>Library books provided:</strong> {formatValue(nocData.library_books_provided)}</div>
          <div><strong>Subjects lacking books:</strong> {nocData.library_lack_equipments || '-'}</div>
          <div><strong>Adequate staff as per UGC norms:</strong> {formatValue(nocData.staff_ugc_norms)}</div>
          <div><strong>Staff deficiencies:</strong> {nocData.staff_teaching_deficiencies || '-'}</div>
          <div><strong>Scholarship details as per Annexure-III:</strong> {formatValue(nocData.staff_as_per_annexure)}</div>
          <div><strong>University ranks secured:</strong> {formatValue(nocData.staff_university_rank)}</div>
          <div><strong>Extension activities conducted:</strong> {formatValue(nocData.staff_extension_activities)}</div>
          <div><strong>Functional website:</strong> {formatValue(nocData.staff_functional_website)}</div>
        </div>
      </div>

      {/* Final Status */}
      <div style={webStyles.section}>
        <h3 style={webStyles.sectionTitle}>✅ Final Decision</h3>
        <div style={webStyles.grid}>
          <div><strong>Final Remarks:</strong> {nocData.final_remarks || '-'}</div>
          <div><strong>Final Status:</strong> {nocData.final_status || '-'}</div>
          {nocData.final_status === 'DeficiencyIdentified' && <div><strong>Processing Fee:</strong> {nocData.processing_fee || '-'}</div>}
        </div>
      </div>

      {renderDeficienciesWeb()}

      <div style={webStyles.footer}>
        <p>College Code: {nocData.coll_code || collegeData.coll_code || '24380'} | Generated on {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
};

// =============== STYLES FOR WEB VIEW ===============
const webStyles = {
  container: {
    maxWidth: 1200,
    margin: '0 auto',
    background: 'white',
    borderRadius: 12,
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #1a472a, #2e7d32)',
    color: 'white',
    padding: 30,
    textAlign: 'center',
  },
  headerTitle: { margin: 0, fontSize: 28 },
  headerSubtitle: { margin: 8, fontWeight: 'normal', opacity: 0.9 },
  headerDate: { marginTop: 15, fontSize: 14, opacity: 0.8 },
  section: { margin: 20, border: '1px solid #e0e4e8', borderRadius: 10, overflow: 'hidden' },
  sectionTitle: { background: '#f5f7fa', padding: '12px 20px', margin: 0, fontSize: 18, borderBottom: '2px solid #2e7d32', color: '#1a472a' },
  grid: { padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 10 },
  footer: { background: '#f5f7fa', padding: 20, textAlign: 'center', fontSize: 12, color: '#666', borderTop: '1px solid #e0e4e8' },
};

// Add global styles for status colors
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    .status-success { color: #2e7d32; font-weight: bold; }
    .status-error { color: #d32f2f; font-weight: bold; }
  `;
  document.head.appendChild(styleSheet);
}

// =============== MAIN COMPONENT ===============
export const CollegeReport = ({ apiData, apiData2, rowData,data3 }) => {
  const [showPDFOption, setShowPDFOption] = React.useState(true);

  if (!apiData) {
    return <div style={{ padding: 20, textAlign: 'center' }}>No data available</div>;
  }

  return (
    <div>
      {showPDFOption && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
          <PDFDownloadLink
            document={<PDFReport data={apiData} data2={apiData2} />}
            fileName="shifting_scrutiny_report.pdf"
            style={{
              background: '#2e7d32',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 40,
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            {/* {({ blob, url, loading, error }) =>
              loading ? 'Generating PDF...' : '📄 Download PDF Report'v  
            } */}
          </PDFDownloadLink>
        </div>
      )}
      <WebViewReport data={apiData} data2={apiData2} rowData={rowData} data3={data3} />
    </div>
  );
};